import React, { useState } from "react";
import googleWorkspaceImage from "../../../../../static/assets/images/get_compliant/google_workspace.png";
import { Alert } from "@mui/material";
import { Button as AquiloButton, OnboardingOrganizationDropdown, ToolTip } from "@truevault/aquilo";
import { useEmailSettings } from "../../../components/superorg/settings/EmailSettings";
import { ACCEPTED_FILE_TYPES, LogoUploadDialog } from "../../../components/dialog/LogoUploadDialog";
import { SURVEY_GATES_SURVEY_NAME } from "../../../components/organisms/survey/SurveyScanEffect";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import clsx from "clsx";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Routes } from "../../../root/routes";
import { useHistory } from "react-router";
import { LoadingPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { usePrimaryOrganization, useSelf } from "../../../hooks";
import { OmniBarProvider } from "../../../../common/hooks/useOmniBar";
import { OmniBar } from "../../../components/dialog/OmniBar";

const TEST_EMAIL_QUESTION_SLUG = "send-test-email";

export function EmailSetupPage() {
  const history = useHistory();
  const [org, ,] = usePrimaryOrganization();
  const [self] = useSelf();

  // TODO(saurabh): Temporary to unblock this page in Railway environment where Google OAuth redirect won't work, and until we add an org switcher to this page
  const skipEmailConnect = new URLSearchParams(window.location.search).get("skip_email") === "1";

  const { settings: emailSettings, organization } = useEmailSettings();

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const {
    answers,
    setAnswer,
    refresh: refreshSurvey,
  } = useSurvey(organization?.id ?? "", SURVEY_GATES_SURVEY_NAME);

  if (!emailSettings || !answers) {
    return <LoadingPageWrapper />;
  }

  let nextDisabled = false;
  let nextDisabledTooltip = "";
  if (!emailSettings.mailboxIsConnected) {
    nextDisabled = true;
    nextDisabledTooltip = "You must connect a mailbox before proceeding.";
  } else if (!emailSettings.messageSettings?.headerImageUrl) {
    nextDisabled = true;
    nextDisabledTooltip = "You must add an email header image before proceeding.";
  } else if (!answers[TEST_EMAIL_QUESTION_SLUG] || answers[TEST_EMAIL_QUESTION_SLUG] === "false") {
    nextDisabled = true;
    nextDisabledTooltip = "You must send a test email before proceeding.";
  }

  // TODO(saurabh): Temporary to unblock this page in Railway environment where Google OAuth redirect won't work, and until we add an org switcher to this page
  if (skipEmailConnect) {
    nextDisabled = false;
    nextDisabledTooltip = null;
  }

  let sendTestEmailButtonDisabled = !emailSettings.mailbox || !emailSettings.mailboxIsConnected;

  return (
    <OmniBarProvider>
      <OmniBar></OmniBar>
      <div className="tw-boundary">
        <div className="absolute top-[177px] right-0 z-0">
          <svg
            width="293"
            height="48"
            viewBox="0 0 293 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M56.8103 11.7928L3.04378 0.194451L2.34503 0.0440731C1.9892 -0.0332102 1.61879 -0.00849442 1.27638 0.115379C0.933968 0.239253 0.633472 0.457246 0.409447 0.744304C0.185423 1.03136 0.0469608 1.37582 0.00999465 1.73807C-0.0269715 2.10031 0.0390622 2.46564 0.200486 2.79203L21.548 46.1438C21.6861 46.4223 21.8892 46.6635 22.1401 46.8472C22.3909 47.0309 22.6822 47.1516 22.9895 47.1992C23.2977 47.2472 23.6129 47.2193 23.908 47.1182C24.203 47.017 24.469 46.8455 24.683 46.6185L39.5104 30.9842C39.6159 30.8702 39.7438 30.7792 39.8862 30.7171C40.0285 30.6549 40.1822 30.6229 40.3375 30.623C40.5487 30.62 40.7566 30.6745 40.9392 30.7806C41.1217 30.8868 41.2719 31.0406 41.3738 31.2255L44.4109 36.8431C44.6048 37.2176 44.9184 37.5166 45.3018 37.6923C45.6852 37.868 46.1164 37.9104 46.5267 37.8127L46.5418 37.8093C46.9571 37.7214 47.3307 37.4963 47.6027 37.1703C47.8746 36.8444 48.0291 36.4365 48.0412 36.0121L48.6496 23.0448C48.6568 22.883 48.6972 22.7244 48.7683 22.5789C48.8446 22.4348 48.9481 22.307 49.0731 22.2025L57.6272 15.1806C57.9084 14.9498 58.1188 14.6445 58.2345 14.2996C58.3501 13.9547 58.3662 13.5842 58.2809 13.2306C58.1956 12.877 58.0125 12.5545 57.7524 12.3002C57.4923 12.0459 57.1658 11.87 56.8103 11.7928ZM57.1387 14.5837L48.5847 21.6056C48.4732 21.6954 48.3722 21.7974 48.2835 21.9098C48.2036 22.0098 48.1346 22.118 48.0777 22.2326C48.018 22.3499 47.971 22.4731 47.9374 22.6003L47.9382 22.6041C47.9028 22.7366 47.8823 22.8726 47.8773 23.0097L47.2691 35.977C47.264 36.232 47.1723 36.4776 47.009 36.6735C46.8458 36.8694 46.6207 37.004 46.3708 37.055L46.3633 37.0567C46.1171 37.1178 45.8574 37.094 45.6264 36.9891C45.3955 36.8842 45.2067 36.7044 45.0907 36.4788L42.0528 30.8574C41.8963 30.5683 41.6685 30.3239 41.391 30.1476C41.1135 29.9712 40.7956 29.8686 40.4673 29.8497L40.4636 29.8505C40.4195 29.8484 40.3755 29.8463 40.3323 29.8479C40.0261 29.848 39.7244 29.9229 39.4537 30.0661C39.2645 30.1644 39.0937 30.2947 38.9488 30.451L24.1215 46.0853C23.9928 46.2206 23.8334 46.323 23.6567 46.3835C23.4801 46.4441 23.2914 46.4611 23.1067 46.4332C22.922 46.4052 22.7469 46.3331 22.596 46.2229C22.4452 46.1128 22.3232 45.9678 22.2404 45.8004L0.892881 2.44873C0.797446 2.25286 0.758944 2.03411 0.781768 1.81742C0.804591 1.60073 0.887817 1.39481 1.02197 1.22311C1.15612 1.05142 1.3358 0.920861 1.54054 0.846306C1.74527 0.771752 1.96683 0.756198 2.17998 0.801424L2.89531 0.956003L56.6451 12.5502C56.8585 12.5964 57.0546 12.7017 57.2109 12.8542C57.3672 13.0067 57.4774 13.2001 57.5289 13.4123C57.5805 13.6245 57.5712 13.8469 57.5022 14.0541C57.4332 14.2612 57.3072 14.4448 57.1387 14.5837Z"
              fill="#5331C6"
            />
            <path
              d="M48.4552 21.9836L48.1357 22.688L47.9384 22.601L47.9375 22.5972L2.63649 2.05591L40.4638 29.8474L40.4675 29.8466L40.5676 29.9234L40.1115 30.5471L39.4538 30.063L2.17669 2.68042C2.03009 2.57497 1.92496 2.4216 1.87947 2.24684C1.83398 2.07208 1.85099 1.88691 1.92757 1.72336C2.00415 1.55982 2.13548 1.42818 2.29884 1.35121C2.4622 1.27424 2.64733 1.25678 2.8222 1.30185C2.86504 1.31231 2.90676 1.32695 2.94674 1.34556L48.2837 21.9067L48.4552 21.9836Z"
              fill="#5331C6"
            />
            <path
              d="M52.2236 37.3805C64.3656 41.8775 75.5707 46.9085 88.8534 46.9085C98.4509 46.9085 108.653 43.7144 117.331 39.6037C126.484 35.2685 135.522 29.5138 143.904 23.5473C149.587 19.5016 155.861 16.5989 162.254 13.8782C172.981 9.31344 184.121 8.1903 195.708 6.92631C210.956 5.26289 226.202 4.54116 241.548 5.02071C252.205 5.35373 263.122 6.99281 273.625 8.65546C279.52 9.58855 287.725 10.0995 292.964 13.243"
              stroke="#5432D0"
              strokeLinecap="round"
              strokeDasharray="6 6"
            />
          </svg>
        </div>
        <div className="flex justify-end pr-[32px]">
          <div>
            <OnboardingOrganizationDropdown
              orgName={org?.name}
              userDisplayName={`${self?.firstName} ${self?.lastName}`}
              showSettings={false}
            />
          </div>
        </div>
        <div
          className="flex items-center mx-auto pt-[10px] z-[2]"
          style={{ flexDirection: "column" }}
        >
          <img
            src="/assets/images/nav-icon-truevault.png"
            alt="TrueVault Logo"
            className="w-[40px] h-[40px]"
          />
          <div className="mt-[12px] text-primary-800 font-bold text-[30px]">
            Let's start by creating your privacy email
          </div>
          <div className="mt-[12px] max-w-[60ch] text-primary-800 font-medium text-[20px] text-center">
            You'll use this email to receive and respond to privacy requests received from your new
            Privacy Center.
          </div>

          <div
            className={clsx(
              "w-[890px] rounded-[24px] bg-white",
              "p-[36px] mt-[24px]",
              "border border-solid border-neutral-300",
              "shadow-[0px_2px_12px_0px_rgba(0,0,0,0.12)]",
            )}
          >
            <div className="flex gap-[12px]">
              <div>
                <StepNumberCircle text="1" />
              </div>
              <div>
                <div className="text-[18px] text-neutral-900 font-medium">
                  Create and connect your privacy email
                </div>
                <div className="mt-[6px] text-[14px] text-neutral-900">
                  Create an email address for your organization to handle the privacy requests and
                  connect it to Polaris so you'll be able to receive the requests. (For example,
                  "privacy@yourcompany.com")
                </div>
                <div className="flex items-center mt-[9px] text-[14px]">
                  Using{" "}
                  <img
                    src={googleWorkspaceImage as string}
                    alt="Google Workspace"
                    // Use `w-auto` so the width will be determined from the height while preserving the original aspect ratio
                    className="ml-[6px] h-[16px] w-auto"
                  />
                  ?
                  <a
                    href="https://knowledge.truevault.com/article/282-add-truevault-polaris-as-a-trusted-app"
                    className="ml-[6px] text-secondary-600 underline font-medium"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Follow the authorization instructions.
                  </a>
                </div>
                <div className="mt-[9px]">
                  {Boolean(emailSettings.mailbox) && emailSettings.mailboxIsConnected ? (
                    <div className="flex gap-[12px]">
                      <AquiloButton size="s" color="tertiary" onClick={emailSettings.deleteMailbox}>
                        Disconnect Email
                      </AquiloButton>
                      <div className="bg-green-100 rounded-[6px] px-[12px] py-[7px] flex items-center justify-between">
                        <div>
                          <CheckCircleIcon className="w-[16px h-[16px] text-green-400" />
                        </div>
                        <div className="ml-[12px] text-green-800 font-medium text-[12px]">
                          {emailSettings.mailbox} is connected
                        </div>
                      </div>
                    </div>
                  ) : (
                    <AquiloButton
                      size="s"
                      color="primary"
                      onClick={() => window.open(emailSettings.mailboxOauthUrl)}
                    >
                      Connect Email
                    </AquiloButton>
                  )}
                  {Boolean(emailSettings.mailbox) && !emailSettings.mailboxIsConnected && (
                    <Alert color="error">
                      There was an issue connecting to your email account; please try again.
                    </Alert>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-[12px] mt-[24px]">
              <div>
                <StepNumberCircle text="2" />
              </div>
              <div>
                <div className="text-[18px] text-neutral-900 font-medium">
                  Add a company logo for email branding
                </div>
                <div className="mt-[6px] text-[14px] text-neutral-900">
                  All emails that Polaris sends to consumers on your behalf will include your
                  company logo at the top.
                  <br />
                  Logo in .PNG or .JPG preferred. Maximum file size is 1MB; maximum height of 100px
                </div>
                {emailSettings.messageSettings?.headerImageUrl && (
                  <div className="relative max-w-[200px] mt-[20px]">
                    <img
                      src={emailSettings.messageSettings?.headerImageUrl}
                      className="max-h-[200px] max-w-[200px]"
                    />
                    <div className="absolute top-[-3px] right-[-25px]">
                      <CloseIcon
                        onClick={() => {
                          emailSettings.updateMessageSettings({ logoUrl: "" });
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className="mt-[12px]">
                  <AquiloButton
                    color={emailSettings.messageSettings?.headerImageUrl ? "tertiary" : "primary"}
                    onClick={() => setDialogOpen(true)}
                  >
                    {emailSettings.messageSettings?.headerImageUrl ? "Change Logo" : "Upload Logo"}
                  </AquiloButton>
                  <LogoUploadDialog
                    acceptedTypes={ACCEPTED_FILE_TYPES}
                    logoType="MESSAGE_HEADER"
                    title="Upload Email Header Logo"
                    organizationId={organization?.id}
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onDone={(logoUrl: string) => emailSettings.updateMessageSettings({ logoUrl })}
                    successMessage={undefined}
                    initialImage={emailSettings?.messageSettings?.headerImageUrl}
                    imageRecommendation={
                      <>
                        Logo in .PNG or .JPG preferred. Maximum file size is 1MB.
                        <br />
                        Maximum height of 100px.
                      </>
                    }
                    omitHelpImage={true}
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-[12px] mt-[24px]">
              <div>
                <StepNumberCircle text="3" />
              </div>
              <div>
                <div className="text-[18px] text-neutral-900 font-medium">
                  Send a test email to see how it looks
                </div>
                <div className="mt-[6px] text-[14px] text-neutral-900">
                  A test email will be sent from your new privacy email to{" "}
                  {emailSettings.testEmailAddress ?? "the email address you used to log in"}.
                </div>
                <div className="mt-[12px]">
                  {answers[TEST_EMAIL_QUESTION_SLUG] &&
                  emailSettings.mailbox &&
                  emailSettings.mailboxIsConnected ? (
                    <div className="flex gap-[12px]">
                      <AquiloButton
                        size="s"
                        color="tertiary"
                        onClick={async () => {
                          await emailSettings.sendTestMessage();
                        }}
                      >
                        Resend Test Email
                      </AquiloButton>
                      <div className="bg-green-100 rounded-[6px] px-[12px] py-[7px] flex items-center justify-between">
                        <div>
                          <CheckCircleIcon className="w-[16px h-[16px] text-green-400" />
                        </div>
                        <div className="ml-[12px] text-green-800 font-medium text-[12px]">
                          A test email was sent from {emailSettings.mailbox}{" "}
                          {answers[TEST_EMAIL_QUESTION_SLUG] &&
                          answers[TEST_EMAIL_QUESTION_SLUG] != "true"
                            ? ` to ${answers[TEST_EMAIL_QUESTION_SLUG]}`
                            : ""}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <ToolTip
                      text={
                        sendTestEmailButtonDisabled
                          ? "Connect mailbox before sending a test email."
                          : null
                      }
                      width="250px"
                      xOffset={-60}
                      yOffset={110}
                    >
                      <AquiloButton
                        size="s"
                        color="primary"
                        disabled={sendTestEmailButtonDisabled}
                        onClick={async () => {
                          try {
                            await emailSettings.sendTestMessage();
                            await setAnswer(
                              TEST_EMAIL_QUESTION_SLUG,
                              emailSettings.testEmailAddress,
                            );
                            await refreshSurvey();
                          } catch (error) {
                            // Error is already handled by useActionRequest hook
                            // Don't set survey answer on failure
                          }
                        }}
                      >
                        Send Test Email
                      </AquiloButton>
                    </ToolTip>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className="mt-[24px] flex justify-center items-center"
          style={{ flexDirection: "column" }}
        >
          <div>
            <ToolTip
              text={nextDisabled ? nextDisabledTooltip : null}
              width="250px"
              xOffset={-60}
              yOffset={110}
            >
              <AquiloButton
                color="primary"
                disabled={nextDisabled}
                onClick={() => history.push(Routes.getCompliant.privacyCenter.websiteSetup)}
              >
                Continue
              </AquiloButton>
            </ToolTip>
          </div>
          <div className="mt-[12px] flex items-center">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.70711 14.7071C7.31658 15.0976 6.68342 15.0976 6.2929 14.7071L2.29289 10.7071C1.90237 10.3166 1.90237 9.68342 2.29289 9.29289L6.29289 5.29289C6.68342 4.90237 7.31658 4.90237 7.70711 5.29289C8.09763 5.68342 8.09763 6.31658 7.70711 6.70711L5.41421 9L17 9C17.5523 9 18 9.44771 18 10C18 10.5523 17.5523 11 17 11L5.41421 11L7.70711 13.2929C8.09763 13.6834 8.09763 14.3166 7.70711 14.7071Z"
                fill="#B8B5C5"
              />
            </svg>
            <a href={Routes.getCompliant.inviteUsers}>
              <div className="ml-[6px] text-neutral-500 text-[14px] font-medium">
                Back to Invite Teammates
              </div>
            </a>
          </div>
          <div className="absolute left-0 mt-[90px] z-0">
            <svg
              width="303"
              height="48"
              viewBox="0 0 303 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M246.19 11.7928L299.956 0.194451L300.655 0.0440731C301.011 -0.0332102 301.381 -0.00849442 301.724 0.115379C302.066 0.239253 302.367 0.457246 302.591 0.744304C302.815 1.03136 302.953 1.37582 302.99 1.73807C303.027 2.10031 302.961 2.46564 302.8 2.79203L281.452 46.1438C281.314 46.4223 281.111 46.6635 280.86 46.8472C280.609 47.0309 280.318 47.1516 280.011 47.1992C279.702 47.2472 279.387 47.2193 279.092 47.1182C278.797 47.017 278.531 46.8455 278.317 46.6185L263.49 30.9842C263.384 30.8702 263.256 30.7792 263.114 30.7171C262.971 30.6549 262.818 30.6229 262.662 30.623C262.451 30.62 262.243 30.6745 262.061 30.7806C261.878 30.8868 261.728 31.0406 261.626 31.2255L258.589 36.8431C258.395 37.2176 258.082 37.5166 257.698 37.6923C257.315 37.868 256.884 37.9104 256.473 37.8127L256.458 37.8093C256.043 37.7214 255.669 37.4963 255.397 37.1703C255.125 36.8444 254.971 36.4365 254.959 36.0121L254.35 23.0448C254.343 22.883 254.303 22.7244 254.232 22.5789C254.155 22.4348 254.052 22.307 253.927 22.2025L245.373 15.1806C245.092 14.9498 244.881 14.6445 244.766 14.2996C244.65 13.9547 244.634 13.5842 244.719 13.2306C244.804 12.877 244.988 12.5545 245.248 12.3002C245.508 12.0459 245.834 11.87 246.19 11.7928ZM245.861 14.5837L254.415 21.6056C254.527 21.6954 254.628 21.7974 254.716 21.9098C254.796 22.0098 254.865 22.118 254.922 22.2326C254.982 22.3499 255.029 22.4731 255.063 22.6003L255.062 22.6041C255.097 22.7366 255.118 22.8726 255.123 23.0097L255.731 35.977C255.736 36.232 255.828 36.4776 255.991 36.6735C256.154 36.8694 256.379 37.004 256.629 37.055L256.637 37.0567C256.883 37.1178 257.143 37.094 257.374 36.9891C257.605 36.8842 257.793 36.7044 257.909 36.4788L260.947 30.8574C261.104 30.5683 261.332 30.3239 261.609 30.1476C261.886 29.9712 262.204 29.8686 262.533 29.8497L262.536 29.8505C262.581 29.8484 262.625 29.8463 262.668 29.8479C262.974 29.848 263.276 29.9229 263.546 30.0661C263.735 30.1644 263.906 30.2947 264.051 30.451L278.878 46.0853C279.007 46.2206 279.167 46.323 279.343 46.3835C279.52 46.4441 279.709 46.4611 279.893 46.4332C280.078 46.4052 280.253 46.3331 280.404 46.2229C280.555 46.1128 280.677 45.9678 280.76 45.8004L302.107 2.44873C302.203 2.25286 302.241 2.03411 302.218 1.81742C302.195 1.60073 302.112 1.39481 301.978 1.22311C301.844 1.05142 301.664 0.920861 301.459 0.846306C301.255 0.771752 301.033 0.756198 300.82 0.801424L300.105 0.956003L246.355 12.5502C246.142 12.5964 245.945 12.7017 245.789 12.8542C245.633 13.0067 245.523 13.2001 245.471 13.4123C245.42 13.6245 245.429 13.8469 245.498 14.0541C245.567 14.2612 245.693 14.4448 245.861 14.5837Z"
                fill="#5331C6"
              />
              <path
                d="M254.545 21.9836L254.864 22.688L255.062 22.601L255.062 22.5972L300.364 2.05591L262.536 29.8474L262.533 29.8466L262.432 29.9234L262.888 30.5471L263.546 30.063L300.823 2.68042C300.97 2.57497 301.075 2.4216 301.121 2.24684C301.166 2.07208 301.149 1.88691 301.072 1.72336C300.996 1.55982 300.865 1.42818 300.701 1.35121C300.538 1.27424 300.353 1.25678 300.178 1.30185C300.135 1.31231 300.093 1.32695 300.053 1.34556L254.716 21.9067L254.545 21.9836Z"
                fill="#5331C6"
              />
              <path
                d="M250.776 37.3805C238.634 41.8775 227.429 46.9085 214.147 46.9085C204.549 46.9085 194.347 43.7144 185.669 39.6037C176.516 35.2685 167.478 29.5138 159.096 23.5473C153.413 19.5016 147.139 16.5989 140.746 13.8782C130.019 9.31344 118.879 8.1903 107.292 6.92631C92.0442 5.26289 76.7977 4.54116 61.4521 5.02071C50.7954 5.35373 39.8776 6.99281 29.3746 8.65546C23.4801 9.58855 15.2754 10.0995 10.0363 13.243"
                stroke="#5432D0"
                strokeLinecap="round"
                strokeDasharray="6 6"
              />
            </svg>
          </div>
        </div>
      </div>
    </OmniBarProvider>
  );
}

export function StepNumberCircle({
  text,
  bgColor,
  textColor,
}: {
  text: string;
  bgColor?: string; // tailwind class name, e.g. "bg-primary-100"
  textColor?: string; // tailwind class name, e.g. "text-primary-600"
}) {
  return (
    <div
      className={clsx(
        "h-[28px] w-[28px] rounded-[100px]",
        "font-bold text-lg",
        "grid place-items-center",
        bgColor || "bg-primary-100",
        textColor || "text-primary-600",
      )}
    >
      {text}
    </div>
  );
}

function CloseIcon({ onClick }: { onClick: () => void }) {
  return (
    <div
      className={clsx(
        "w-[38px] h-[38px]",
        "bg-white",
        "border border-solid border-neutral-300 rounded-[19px]",
        "grid place-items-center",
        "cursor-pointer",
      )}
      onClick={onClick}
    >
      <XMarkIcon className="w-[20px] h-[20px] text-neutral-500" />
    </div>
  );
}
